export interface OFXTransaction {
  // Referência que o OktonBank grava e usa para deduplicar. Não é o FITID do
  // banco: veja referenciaEstavel.
  referencia: string;
  // FITID como o banco mandou, só para exibir e conferir. Pode vir nulo.
  fitId: string | null;
  type: string;
  date: string; // ISO date yyyy-MM-dd
  // HH:mm:ss do DTPOSTED, quando o arquivo traz a hora. Não entra na
  // referência: reimportar um OFX de antes da hora existir não pode duplicar.
  time: string | null;
  amount: number;
  memo: string;
  checkNum: string | null;
  refNum: string | null;
}

export interface OFXStatement {
  bankId: string | null;
  branchId: string | null;
  accountId: string | null;
  accountType: string | null;
  currency: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  balance: number | null;
  balanceDate: string | null;
  transactions: OFXTransaction[];
  // Transações descartadas na leitura, por virem sem DTPOSTED ou sem TRNAMT
  // utilizável. Entram na contagem para a tela avisar, em vez de virarem data
  // inválida ou valor zero no banco.
  semData: number;
}

export interface OFXData {
  statements: OFXStatement[];
}

// ─── Referência estável ───
//
// O FITID não serve como chave. Nos arquivos reais dos bancos da lista:
//   - Banco do Brasil repete o mesmo FITID em duas transações diferentes do
//     mesmo dia (mesmo CHECKNUM inclusive), então uma das duas sumiria no
//     INSERT IGNORE;
//   - Sicredi numera o FITID pela posição na exportação (COB000001-1, ' -4'),
//     então o mesmo lançamento muda de FITID a cada período exportado, e um
//     lançamento novo cai no FITID de um antigo;
//   - Bradesco embute o histórico no FITID e passa de 100 caracteres, perto
//     demais do varchar(120) da referencia_exb.
//
// A referência é derivada só de data, valor, descrição e da ordem entre
// transações idênticas no mesmo dia. Isso é estável entre exportações (o banco
// exporta o dia inteiro, então a ordem dentro de um dia não muda) e distingue
// duas tarifas iguais ou dois PIX de mesmo valor, que é o caso que o FITID do
// banco deveria cobrir e não cobre.
//
// Tudo que entra na referência está gravado na extratobancario, de propósito:
// a referência de uma linha pode ser conferida a partir da própria tabela,
// sem depender do arquivo OFX que a trouxe.

// Dois acumuladores multiplicativos com módulos primos distintos, combinados
// em 16 hexadecimais. Sem operadores bit a bit, vetados pelo eslint.
const MODULO_A = 2147483647; // 2^31 - 1
const MODULO_B = 1073741789; // maior primo abaixo de 2^30

function hashTexto(base: string): string {
  let a = 7;
  let b = 131;
  for (let i = 0; i < base.length; i += 1) {
    const c = base.charCodeAt(i);
    a = (a * 31 + c) % MODULO_A;
    b = (b * 37 + c * (i + 1)) % MODULO_B;
  }
  const hexA = a.toString(16).toUpperCase().padStart(8, '0');
  const hexB = b.toString(16).toUpperCase().padStart(8, '0');
  return `${hexA}${hexB}`;
}

// Data e valor entram em claro para a referência poder ser conferida a olho na
// tabela; só a descrição é resumida em hash. Colidir exige mesmo dia, mesmo
// valor, mesma descrição e mesma ordem — ou seja, a mesma transação.
// Formato: OFX20240115D12000-3F9A2B1C7E5D0148-0  (~36 caracteres)
export function referenciaEstavel(
  data: string,
  valor: number,
  memo: string,
  ordem: number,
): string {
  const dia = data.replace(/-/g, '');
  const sinal = valor < 0 ? 'D' : 'C';
  const centavos = Math.round(Math.abs(valor) * 100);
  return `OFX${dia}${sinal}${centavos}-${hashTexto(memo)}-${ordem}`;
}

// ─── Leitura do arquivo ───

const ENTIDADES: Record<string, string> = {
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

// OFX 2.x é XML e escapa & < >; alguns geradores 1.x também escapam. Sem
// decodificar, "JOAO &amp; CIA" chega assim na descrição do lançamento.
// O &amp; é resolvido por último para "&amp;lt;" não virar "<".
function decodificarEntidades(texto: string): string {
  return texto
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) =>
      String.fromCodePoint(parseInt(hex, 16)),
    )
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec, 10)))
    .replace(/&(lt|gt|quot|apos|nbsp);/gi, (inteiro, nome) => {
      const valor = ENTIDADES[String(nome).toLowerCase()];
      return valor === undefined ? inteiro : valor;
    })
    .replace(/&amp;/gi, '&');
}

function extractTag(content: string, tag: string): string | null {
  // Pega tanto <TAG>valor quanto <TAG>valor</TAG>
  const re = new RegExp(`<${tag}>([^<\\r\\n]*)`, 'i');
  const m = re.exec(content);
  if (!m) return null;
  const bruto = m[1].trim();
  return bruto === '' ? null : decodificarEntidades(bruto);
}

// A spec do OFX manda ponto como separador decimal, e é o que todos os
// arquivos reais dos bancos da lista usam. O tratamento de vírgula é defesa
// contra gerador fora do padrão: sem ele "-1.234,56" viraria -1.234 — erro de
// mil vezes, calado. Ponto sozinho continua sendo decimal, como manda a spec.
function parseValor(bruto: string | null): number {
  if (bruto === null) return NaN;
  const limpo = bruto.replace(/\s/g, '');
  const temPonto = limpo.includes('.');
  const temVirgula = limpo.includes(',');
  let normalizado = limpo;
  if (temPonto && temVirgula) {
    const decimal = limpo.lastIndexOf('.') > limpo.lastIndexOf(',') ? '.' : ',';
    const milhar = decimal === '.' ? ',' : '.';
    normalizado = limpo.split(milhar).join('').replace(decimal, '.');
  } else if (temVirgula) {
    normalizado = limpo.replace(',', '.');
  }
  return parseFloat(normalizado);
}

// Datas OFX: YYYYMMDD ou YYYYMMDDHHMMSS[.mmm][fuso]. O fuso é descartado: os
// bancos brasileiros gravam a data local com hora 00:00 ou 12:00, então o dia
// já vem certo. Retorna null quando não há 8 dígitos de data.
function parseOFXDate(raw: string | null): string | null {
  if (raw === null) return null;
  const digits = raw.replace(/[^0-9]/g, '');
  if (digits.length < 8) return null;
  const year = digits.substring(0, 4);
  const month = digits.substring(4, 6);
  const day = digits.substring(6, 8);
  if (year === '0000' || month === '00' || day === '00') return null;
  return `${year}-${month}-${day}`;
}

// DTPOSTED é YYYYMMDDHHMMSS[.XXX][fuso], com a hora opcional na spec. Vale a
// hora como veio, sem converter o fuso, pelo mesmo motivo da data. A hora que
// o banco preenche igual em tudo (000000) é tratada no back, que vê o lote.
function parseOFXTime(raw: string | null): string | null {
  if (raw === null) return null;
  const digits = raw.split(/[.[]/)[0].replace(/[^0-9]/g, '');
  if (digits.length < 12) return null;
  const hora = Number(digits.substring(8, 10));
  const minuto = Number(digits.substring(10, 12));
  const segundo = digits.length >= 14 ? Number(digits.substring(12, 14)) : 0;
  if (hora > 23 || minuto > 59 || segundo > 59) return null;
  const doisDigitos = (n: number) => String(n).padStart(2, '0');
  return `${doisDigitos(hora)}:${doisDigitos(minuto)}:${doisDigitos(segundo)}`;
}

interface TransacaoBruta {
  fitId: string | null;
  type: string;
  date: string;
  time: string | null;
  amount: number;
  memo: string;
  checkNum: string | null;
  refNum: string | null;
}

// descricao_exb é varchar(255). Cortar aqui, e não no back, mantém a descrição
// gravada idêntica à que gerou a referência: cortada depois, reimportar o
// mesmo OFX geraria outra referência e a linha entraria duplicada.
const LIMITE_DESCRICAO = 255;

function lerBloco(block: string): TransacaoBruta | null {
  const dtPosted = extractTag(block, 'DTPOSTED');
  const date = parseOFXDate(dtPosted);
  // Sem data não dá para lançar. Antes isto virava a string '--', que passava
  // pela validação do back e chegava ao MySQL como data inválida.
  if (date === null) return null;
  const amount = parseValor(extractTag(block, 'TRNAMT'));
  if (Number.isNaN(amount)) return null;
  return {
    fitId: extractTag(block, 'FITID'),
    type: extractTag(block, 'TRNTYPE') ?? '',
    date,
    time: parseOFXTime(dtPosted),
    amount,
    memo: (extractTag(block, 'MEMO') ?? extractTag(block, 'NAME') ?? '').slice(
      0,
      LIMITE_DESCRICAO,
    ),
    checkNum: extractTag(block, 'CHECKNUM'),
    refNum: extractTag(block, 'REFNUM'),
  };
}

function parseTransactions(sgml: string): {
  transacoes: TransacaoBruta[];
  semData: number;
} {
  const brutas: (TransacaoBruta | null)[] = [];

  // Formato de tag fechada (<STMTTRN>...</STMTTRN>)
  const closedRe = /<STMTTRN>([\s\S]*?)<\/STMTTRN>/gi;
  let m: RegExpExecArray | null = closedRe.exec(sgml);
  while (m !== null) {
    brutas.push(lerBloco(m[1]));
    m = closedRe.exec(sgml);
  }

  // Sem tag de fechamento (SGML de tag aberta, caso do Bradesco), cada pedaço
  // vai até o próximo <STMTTRN>.
  if (brutas.length === 0) {
    sgml
      .split(/<STMTTRN>/i)
      .slice(1)
      .forEach(chunk => brutas.push(lerBloco(chunk)));
  }

  const transacoes = brutas.filter((t): t is TransacaoBruta => t !== null);
  return { transacoes, semData: brutas.length - transacoes.length };
}

// A ordem é contada dentro do grupo (data, valor, descrição), não pela posição
// no arquivo. Assim exportar um período diferente não muda a referência: só
// duas transações realmente idênticas no mesmo dia recebem ordem 0 e 1.
function comReferencia(transacoes: TransacaoBruta[]): OFXTransaction[] {
  const ordinais = new Map<string, number>();
  return transacoes.map(t => {
    const grupo = `${t.date}|${t.amount}|${t.memo}`;
    const ordem = ordinais.get(grupo) ?? 0;
    ordinais.set(grupo, ordem + 1);
    return {
      ...t,
      referencia: referenciaEstavel(t.date, t.amount, t.memo, ordem),
    };
  });
}

function lerExtrato(bloco: string): OFXStatement {
  const { transacoes, semData } = parseTransactions(bloco);
  const saldo = parseValor(extractTag(bloco, 'BALAMT'));
  return {
    bankId: extractTag(bloco, 'BANKID'),
    branchId: extractTag(bloco, 'BRANCHID'),
    accountId: extractTag(bloco, 'ACCTID'),
    accountType: extractTag(bloco, 'ACCTTYPE'),
    currency: extractTag(bloco, 'CURDEF'),
    periodStart: parseOFXDate(extractTag(bloco, 'DTSTART')),
    periodEnd: parseOFXDate(extractTag(bloco, 'DTEND')),
    balance: Number.isNaN(saldo) ? null : saldo,
    balanceDate: parseOFXDate(extractTag(bloco, 'DTASOF')),
    transactions: comReferencia(transacoes),
    semData,
  };
}

export function parseOFX(fileContent: string): OFXData {
  const content = fileContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Descarta o cabeçalho OFX/SGML, tudo que vem antes de <OFX>
  const ofxIdx = content.search(/<OFX>/i);
  const body = ofxIdx !== -1 ? content.substring(ofxIdx) : content;

  // Um arquivo pode trazer várias contas, cada uma no seu <STMTRS>. Antes as
  // transações de todas elas eram somadas num bolo só e iam para a conta
  // escolhida na tela; agora cada extrato fica separado.
  const partes = body.split(/<(?:CC)?STMTRS>/i).slice(1);
  const statements = (partes.length > 0 ? partes : [body])
    .map(lerExtrato)
    .filter(e => e.transactions.length > 0 || e.semData > 0);

  if (statements.length === 0) {
    throw new Error(
      'Nenhuma transação encontrada no arquivo OFX. Verifique se o arquivo é válido.',
    );
  }

  return { statements };
}

// ─── Codificação ───
//
// Ler sempre em latin1 corrompia todo arquivo UTF-8: "Antecipação" chegava
// como "AntecipaÃ§Ã£o". Acontece no OFX 2.x (XML, UTF-8 por padrão) e em
// bancos que declaram CHARSET:1252 no cabeçalho mas gravam UTF-8 no corpo.
//
// Por isso o cabeçalho é só um palpite: o que decide é tentar decodificar como
// UTF-8 em modo estrito. Texto em português no CP1252 quase nunca forma UTF-8
// válido ("ção" é E7 E3, sequência inválida), então um arquivo que passa no
// modo estrito e tem byte acima de 0x7F é UTF-8 de verdade.
function charsetDoCabecalho(amostra: string): string {
  const xml = /<\?xml[^>]*encoding=["']([^"']+)["']/i.exec(amostra);
  if (xml) return xml[1].toLowerCase();
  const charset = /^\s*CHARSET:\s*(\S+)/im.exec(amostra);
  if (charset) {
    const valor = charset[1].toLowerCase();
    if (valor === '1252' || valor === 'cp1252') return 'windows-1252';
    return valor;
  }
  const encoding = /^\s*ENCODING:\s*(\S+)/im.exec(amostra);
  if (encoding && encoding[1].toLowerCase().includes('utf')) return 'utf-8';
  return 'windows-1252';
}

function decodifica(bytes: Uint8Array, charset: string): string | null {
  try {
    return new TextDecoder(charset).decode(bytes);
  } catch {
    return null;
  }
}

export function decodificarOFX(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);

  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return decodifica(bytes.subarray(3), 'utf-8') ?? '';
  }

  const temByteAlto = bytes.some(b => b > 0x7f);
  if (temByteAlto) {
    try {
      // fatal: true faz o decode falhar em vez de devolver caracteres de
      // substituição — é isso que distingue UTF-8 real de CP1252.
      return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
      // Não é UTF-8; segue para o charset declarado no cabeçalho.
    }
  }

  // Só os primeiros bytes: o cabeçalho vem antes de <OFX>.
  const amostra = decodifica(bytes.subarray(0, 512), 'latin1') ?? '';
  const charset = charsetDoCabecalho(amostra);
  return (
    decodifica(bytes, charset) ??
    decodifica(bytes, 'windows-1252') ??
    decodifica(bytes, 'latin1') ??
    ''
  );
}
